import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Calendar, AlertTriangle, 
  Clock, DollarSign, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentAgendaPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar'); // calendar | list
  
  // Current month/year
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // Fetch agenda
  const fetchAgenda = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/purchases/agenda-pagos?mes=${currentMonth}&anio=${currentYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgenda(response.data);
    } catch (error) {
      console.error('Error fetching agenda:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentMonth, currentYear]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  // Navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 2, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format currency
  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  // Get month name
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Generate calendar grid
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday

    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, events: [] });
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = agenda.filter(a => a.fecha_vencimiento === dateStr);
      days.push({ day, dateStr, events: dayEvents });
    }
    
    return days;
  };

  // Get urgency color
  const getUrgencyColor = (urgencia) => {
    switch (urgencia) {
      case 'rojo': return 'bg-red-500';
      case 'ambar': return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  };

  const getUrgencyBadge = (item) => {
    if (item.urgencia === 'rojo') {
      return <Badge className="bg-red-100 text-red-700 border-red-300">Vencida</Badge>;
    }
    if (item.urgencia === 'ambar') {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-300">Próxima</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 border-slate-300">Futura</Badge>;
  };

  // Group by week for list view
  const groupByWeek = () => {
    const sorted = [...agenda].sort((a, b) => {
      // Vencidas first, then by date
      if (a.urgencia === 'rojo' && b.urgencia !== 'rojo') return -1;
      if (b.urgencia === 'rojo' && a.urgencia !== 'rojo') return 1;
      return new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento);
    });
    return sorted;
  };

  // Calculate totals
  const totals = agenda.reduce((acc, item) => {
    acc.total += item.total;
    if (item.urgencia === 'rojo') acc.vencidas += item.total;
    else if (item.urgencia === 'ambar') acc.proximas += item.total;
    else acc.futuras += item.total;
    return acc;
  }, { total: 0, vencidas: 0, proximas: 0, futuras: 0 });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/compras')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display text-2xl text-slate-900">Agenda de Pagos</h1>
            <p className="text-slate-500 text-sm">Calendario de vencimientos de cuentas por pagar</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="calendar">Calendario</SelectItem>
              <SelectItem value="list">Lista</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-slate-900">{formatCurrency(totals.total)}</div>
            <div className="text-xs text-slate-500">{agenda.length} pagos pendientes</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <div>
                <div className="text-xl font-medium text-red-700">{formatCurrency(totals.vencidas)}</div>
                <div className="text-xs text-red-600">Vencidas</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <div>
                <div className="text-xl font-medium text-amber-700">{formatCurrency(totals.proximas)}</div>
                <div className="text-xs text-amber-600">Próx. 7 días</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                <div className="text-xl font-medium text-slate-700">{formatCurrency(totals.futuras)}</div>
                <div className="text-xs text-slate-500">Futuras</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {viewMode === 'calendar' ? (
        /* Calendar View */
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-lg font-medium">
                  {monthNames[currentMonth - 1]} {currentYear}
                </h2>
                <Button variant="outline" size="sm" onClick={goToNextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Hoy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
              {/* Header */}
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                <div key={day} className="bg-slate-50 p-2 text-center text-sm font-medium text-slate-600">
                  {day}
                </div>
              ))}
              
              {/* Days */}
              {loading ? (
                <div className="col-span-7 bg-white p-8 text-center text-slate-500">
                  Cargando...
                </div>
              ) : (
                generateCalendarDays().map((dayData, idx) => {
                  const isToday = dayData.dateStr === new Date().toISOString().split('T')[0];
                  
                  return (
                    <div 
                      key={idx} 
                      className={`bg-white min-h-[100px] p-2 ${
                        dayData.day ? '' : 'bg-slate-50'
                      } ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                    >
                      {dayData.day && (
                        <>
                          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                            {dayData.day}
                          </div>
                          <div className="space-y-1">
                            {dayData.events.slice(0, 3).map((event, eIdx) => (
                              <div
                                key={eIdx}
                                onClick={() => navigate(`/admin/compras/${event.id}`)}
                                className={`text-xs p-1 rounded cursor-pointer truncate ${
                                  event.urgencia === 'rojo' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                                  event.urgencia === 'ambar' ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' :
                                  'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                                title={`${event.proveedor_nombre} - ${formatCurrency(event.total)}`}
                              >
                                <div className="font-medium truncate">{event.proveedor_nombre}</div>
                                <div>{formatCurrency(event.total)}</div>
                              </div>
                            ))}
                            {dayData.events.length > 3 && (
                              <div className="text-xs text-slate-500 text-center">
                                +{dayData.events.length - 3} más
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                <span className="text-slate-600">Vencida</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-500"></div>
                <span className="text-slate-600">Próx. 7 días</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-400"></div>
                <span className="text-slate-600">Futura</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Urgencia</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Vencimiento</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Compra</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Proveedor</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Factura</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Total</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Días</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Acción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td>
                  </tr>
                ) : groupByWeek().length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">
                      No hay pagos pendientes
                    </td>
                  </tr>
                ) : (
                  groupByWeek().map((item) => (
                    <tr 
                      key={item.id} 
                      className={`border-b hover:bg-slate-50 ${
                        item.urgencia === 'rojo' ? 'bg-red-50' :
                        item.urgencia === 'ambar' ? 'bg-amber-50' : ''
                      }`}
                    >
                      <td className="p-4">
                        <div className={`w-3 h-3 rounded-full ${getUrgencyColor(item.urgencia)}`}></div>
                      </td>
                      <td className="p-4">
                        <span className={item.urgencia === 'rojo' ? 'text-red-700 font-medium' : ''}>
                          {item.fecha_vencimiento}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-slate-700">{item.numero_compra}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-slate-900">{item.proveedor_nombre}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-600">{item.numero_factura || '-'}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-medium">{formatCurrency(item.total)}</span>
                      </td>
                      <td className="p-4">
                        {item.dias_para_vencer < 0 ? (
                          <span className="text-red-700 font-medium">{Math.abs(item.dias_para_vencer)}d vencido</span>
                        ) : item.dias_para_vencer === 0 ? (
                          <span className="text-amber-700 font-medium">Hoy</span>
                        ) : (
                          <span className="text-slate-600">en {item.dias_para_vencer}d</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/compras/${item.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentAgendaPage;
